import { zodResolver } from '@hookform/resolvers/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Divider, Stack } from '@mui/material'
import { createTeamMember, teamMemberCreatedMessage } from 'api/teams'
import {
  CaregiverRoleFields,
  combineRoleValues,
  splitRolesIntoCategories,
} from 'components/form-elements/CaregiverRoleFields'
import FormAutocomplete from 'components/form-elements/FormAutocomplete'
import FormInput from 'components/form-elements/FormInput'
import NotificationSys from 'components/NotificationSystem'
import { EMAIL_IS_INVALID, minButtonWidth, PHONE_IS_INVALID } from 'const'
import { useCharactersLeft } from 'hooks/useCharactersLeft'
import React, { useEffect, useMemo } from 'react'
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useMutation } from 'react-query'
import { TeamMember, TeamMemberCreateRequest, TraineeTeamRole } from 'types'
import { array, literal, number, object, string, TypeOf, union } from 'zod'
import { MIN_SEARCH_LENGTH, useTeamMemberSearch } from './useTeamMemberSearch'

const required = string().min(1, 'Required')
const optional = string().optional()

interface TimelineTeamMemberFormAddProps {
  onClose: () => void
  teamId: number
  teamMembersData: TeamMember[]
  organizationId?: number
  onRefetchTeamMembers: () => Promise<void>
}

export function TimelineTeamMemberFormAdd({
  onClose,
  teamId,
  teamMembersData,
  organizationId,
  onRefetchTeamMembers,
}: TimelineTeamMemberFormAddProps) {
  const formSchema = object({
    memberSelect: union([number(), string().transform((val) => parseInt(val, 10))])
      .optional()
      .nullable(),
    firstName: required,
    lastName: optional,
    email: string().email(EMAIL_IS_INVALID).optional().or(literal('')),
    about: string().max(200, 'Max message length is 200 characters').optional(),
    professionalRole: array(
      object({
        label: string(),
        value: string(),
      }),
    ).optional(),
    familyRole: array(
      object({
        label: string(),
        value: string(),
      }),
    ).optional(),
    otherRole: optional,
    addressStreet: optional,
    addressZipCode: optional,
    addressCountry: optional,
    addressCity: optional,
    addressState: optional,
    phone: string().min(10, PHONE_IS_INVALID).or(literal('')).optional(),
    comments: string(),
  })

  type FormType = TypeOf<typeof formSchema>

  const methods = useForm<FormType>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: {
      memberSelect: undefined,
      firstName: '',
      lastName: '',
      email: '',
      about: '',
      professionalRole: [],
      familyRole: [],
      addressStreet: '',
      addressZipCode: '',
      addressCountry: '',
      addressCity: '',
      addressState: '',
      phone: '',
      comments: '',
    },
  })
  const { handleSubmit, watch, setValue } = methods

  const memberSelectValue = useWatch({ name: 'memberSelect', control: methods.control })

  const { teamMembersOptions, isLoading, debouncedSearch, selectedMember, hasMinSearchLength } =
    useTeamMemberSearch({
      teamMembersData,
      selectedMemberId: memberSelectValue,
      organizationId,
    })

  const createMutation = useMutation(createTeamMember, {
    onSuccess: async (response) => {
      NotificationSys.showSuccess(teamMemberCreatedMessage(response.data?.inviteSentVia))
      onClose()
      await onRefetchTeamMembers()
    },
  })

  const onSubmitHandler: SubmitHandler<FormType> = (values: FormType) => {
    if (!teamId) {
      NotificationSys.showWarning('No team selected')
      return
    }

    const role = combineRoleValues(values.professionalRole, values.familyRole)
    const otherRole = role.includes(TraineeTeamRole.other) ? values.otherRole || '' : ''

    const userId = values.memberSelect ?? 0

    const request: TeamMemberCreateRequest = {
      id: teamId,
      userId,
      email: values.email || '',
      firstName: values.firstName,
      lastName: values.lastName || '',
      about: values.about || '',
      role,
      otherRole,
      street: values.addressStreet || '',
      zipCode: values.addressZipCode || '',
      country: values.addressCountry || '',
      city: values.addressCity || '',
      state: values.addressState || '',
      phone: values.phone || '',
      comments: values.comments || '',
    }

    createMutation.mutate(request)
  }

  const aboutMeValue = watch('about')
  const charactersLeft = useCharactersLeft(200, aboutMeValue?.length || 0)

  const hasActiveMember = useMemo(
    () => memberSelectValue !== null && memberSelectValue !== undefined,
    [memberSelectValue],
  )

  // Fill form when selectedMember changes
  useEffect(() => {
    if (selectedMember) {
      setValue('firstName', selectedMember.firstname || '')
      setValue('lastName', selectedMember.lastname || '')
      setValue('email', selectedMember.email || '')
      setValue('about', selectedMember.about || '')
      setValue('phone', selectedMember.phoneNumber || '')

      const { professionalRole, familyRole } = splitRolesIntoCategories(selectedMember.role)
      setValue('professionalRole', professionalRole)
      setValue('familyRole', familyRole)
    } else {
      setValue('firstName', '')
      setValue('lastName', '')
      setValue('email', '')
      setValue('about', '')
      setValue('phone', '')
      setValue('professionalRole', [])
      setValue('familyRole', [])
    }
  }, [selectedMember, setValue])

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={handleSubmit(onSubmitHandler)} width="100%" noValidate>
        <Stack spacing={1.5} mb={2}>
          <FormAutocomplete
            name="memberSelect"
            label="Select Member"
            options={teamMembersOptions}
            isLoading={isLoading}
            onChangeInput={debouncedSearch}
            defaultValue={null}
            noOptionsText={
              hasMinSearchLength
                ? 'No members found'
                : `Type at least ${MIN_SEARCH_LENGTH} characters to search`
            }
          />

          <Divider>OR</Divider>

          <FormInput label="First Name" name="firstName" fullWidth disabled={hasActiveMember} />

          <FormInput label="Last Name" name="lastName" fullWidth disabled={hasActiveMember} />

          <FormInput label="Email" name="email" fullWidth disabled={hasActiveMember} />

          <CaregiverRoleFields />

          <FormInput label="Mobile Phone Number" name="phone" fullWidth />

          <FormInput label="Street Address" name="addressStreet" fullWidth />
          <FormInput label="Zip Code" name="addressZipCode" fullWidth />
          <FormInput label="City" name="addressCity" fullWidth />
          <FormInput label="State" name="addressState" fullWidth />
          <FormInput label="Country" name="addressCountry" fullWidth />

          <FormInput
            name="about"
            label="What you might want to know about me"
            placeholder="Please enter the relevant information about you"
            fullWidth
            minRows={3}
            maxRows={6}
            multiline
            helperText={charactersLeft}
            checkTouchField={false}
            disabled={hasActiveMember}
          />

          <FormInput label="Comments" name="comments" fullWidth multiline />
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
          <Button variant="outlined" onClick={onClose} sx={minButtonWidth}>
            Close
          </Button>
          <LoadingButton loading={false} variant="contained" type="submit" sx={minButtonWidth}>
            Save
          </LoadingButton>
        </Stack>
      </Box>
    </FormProvider>
  )
}
